import { useEffect, useRef, useState } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../config/permissions";

const MODES = Object.freeze(["strict", "normal", "relaxed"]);
const RADIUS_AUTOSAVE_DELAY_MS = 500;
export const DEFAULT_BOOKING_ALERT_CONFIGURATION = Object.freeze({
  mode: "normal",
  alertsEnabled: true,
  pickupArrivalRadiusMeters: 100,
  dropoffArrivalRadiusMeters: 100,
  deviationRadiusMeters: 500,
  deviationRadiusMaximumMeters: 500,
});
export const DEFAULT_BOOKING_ALERT_LIMITS = Object.freeze({
  MIN_ARRIVAL_RADIUS_METERS: 25,
  MAX_ARRIVAL_RADIUS_METERS: 1000,
  MIN_ROUTE_DEVIATION_RADIUS_METERS: 50,
});

function normalizedConfiguration(configuration) {
  const source = configuration || {};
  const deviationRadiusMeters = source.deviationRadiusMeters ?? source.routeDeviationRadiusMeters ??
    DEFAULT_BOOKING_ALERT_CONFIGURATION.deviationRadiusMeters;
  return {
    ...DEFAULT_BOOKING_ALERT_CONFIGURATION,
    ...source,
    deviationRadiusMeters,
    deviationRadiusMaximumMeters: source.deviationRadiusMaximumMeters ??
      source.routeDeviationMaximumRadiusMeters ?? deviationRadiusMeters,
  };
}

function normalizedLimits(limits) {
  return { ...DEFAULT_BOOKING_ALERT_LIMITS, ...(limits || {}) };
}

function validPayload(values, limits, deviationInput, maximumInput) {
  if (!values || !MODES.includes(values.mode) || typeof values.alertsEnabled !== "boolean") return null;
  if (!/^\d+$/.test(String(deviationInput)) || !/^\d+$/.test(String(maximumInput))) return null;
  const deviationRadiusMeters = Number(deviationInput);
  const deviationRadiusMaximumMeters = Number(maximumInput);
  const minimum = Number(limits?.MIN_ROUTE_DEVIATION_RADIUS_METERS);
  if (!Number.isSafeInteger(deviationRadiusMeters) || !Number.isSafeInteger(deviationRadiusMaximumMeters) ||
      deviationRadiusMaximumMeters < minimum || deviationRadiusMeters < minimum ||
      deviationRadiusMeters > deviationRadiusMaximumMeters) return null;
  return {
    mode: values.mode,
    alertsEnabled: values.alertsEnabled,
    pickupArrivalRadiusMeters: values.pickupArrivalRadiusMeters,
    dropoffArrivalRadiusMeters: values.dropoffArrivalRadiusMeters,
    deviationRadiusMeters,
    deviationRadiusMaximumMeters,
  };
}

export default function BookingAlertConfigurationPanel({
  bookingId,
  configuration,
  limits,
  lifecycleEditable = true,
  onPreview,
  onSaved,
}) {
  const { user } = useAuth() || {};
  const role = normalizeRole(user?.role);
  const authorized = role === "technical_admin" || role === "admin";
  const canEdit = authorized && lifecycleEditable;
  const effectiveLimits = normalizedLimits(limits);
  const [values, setValues] = useState(() => normalizedConfiguration(configuration));
  const [deviationInput, setDeviationInput] = useState(() =>
    String(normalizedConfiguration(configuration).deviationRadiusMeters));
  const [maximumInput, setMaximumInput] = useState(() =>
    String(normalizedConfiguration(configuration).deviationRadiusMaximumMeters));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const autosaveTimer = useRef(null);

  function cancelAutosave() {
    if (autosaveTimer.current !== null) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
  }

  useEffect(() => {
    cancelAutosave();
    const next = normalizedConfiguration(configuration);
    setValues(next);
    setDeviationInput(String(next.deviationRadiusMeters));
    setMaximumInput(String(next.deviationRadiusMaximumMeters));
    setMessage("");
  }, [bookingId, configuration]);

  useEffect(() => () => cancelAutosave(), []);

  function preview(next) {
    setValues(next);
    setMessage("");
    onPreview?.(next);
  }

  function updateDeviation(value) {
    if (!canEdit || saving) return;
    setDeviationInput(value);
    const numeric = Number(value);
    if (/^\d+$/.test(value) && Number.isSafeInteger(numeric) &&
        numeric >= effectiveLimits.MIN_ROUTE_DEVIATION_RADIUS_METERS &&
        numeric <= values.deviationRadiusMaximumMeters) {
      const next = { ...values, deviationRadiusMeters: numeric };
      preview(next);
      queueAutosave(next, value, maximumInput);
    } else {
      cancelAutosave();
      setMessage(`Choose a whole-number deviation from 50 to ${values.deviationRadiusMaximumMeters} meters.`);
    }
  }

  function updateMaximum(value) {
    if (!canEdit || saving) return;
    setMaximumInput(value);
    const numeric = Number(value);
    if (/^\d+$/.test(value) && Number.isSafeInteger(numeric) &&
        numeric >= effectiveLimits.MIN_ROUTE_DEVIATION_RADIUS_METERS) {
      const nextDeviation = Math.min(Number(deviationInput), numeric);
      setDeviationInput(String(nextDeviation));
      const next = { ...values, deviationRadiusMeters: nextDeviation, deviationRadiusMaximumMeters: numeric };
      preview(next);
      queueAutosave(next, String(nextDeviation), value);
    } else {
      cancelAutosave();
      setMessage("Use a whole number of at least 50 meters.");
    }
  }

  async function persist(payload, successMessage) {
    const { data } = await api.put(`/gps/bookings/${bookingId}/alert-configuration`, payload);
    const saved = normalizedConfiguration(data?.configuration || payload);
    setValues(saved);
    setDeviationInput(String(saved.deviationRadiusMeters));
    setMaximumInput(String(saved.deviationRadiusMaximumMeters));
    await onSaved?.(saved);
    setMessage(successMessage);
    return saved;
  }

  function queueAutosave(nextValues, nextDeviationInput, nextMaximumInput) {
    cancelAutosave();
    const payload = validPayload(nextValues, effectiveLimits, nextDeviationInput, nextMaximumInput);
    if (!payload || !canEdit || !bookingId) return;
    setMessage("Saving radius automatically...");
    autosaveTimer.current = setTimeout(async () => {
      autosaveTimer.current = null;
      try {
        setSaving(true);
        await persist(payload, "Radius saved automatically for this booking.");
      } catch (error) {
        setMessage(error?.response?.data?.message ||
          "Unable to automatically save this booking's radius.");
      } finally {
        setSaving(false);
      }
    }, RADIUS_AUTOSAVE_DELAY_MS);
  }

  async function toggleAlerts(enabled) {
    if (!canEdit || saving || !bookingId) return;
    cancelAutosave();
    const previous = values;
    const next = { ...values, alertsEnabled: enabled };
    const payload = validPayload(next, effectiveLimits, deviationInput, maximumInput);
    if (!payload) {
      setMessage("Enter a valid route-deviation radius before changing alerts.");
      return;
    }
    preview(next);
    try {
      setSaving(true);
      await persist(payload, `Deviation alerts turned ${enabled ? "ON" : "OFF"} for this booking.`);
    } catch (error) {
      setValues(previous);
      onPreview?.(previous);
      setMessage(error?.response?.data?.message || "Unable to update this booking's alerts.");
    } finally {
      setSaving(false);
    }
  }

  if (!bookingId) return null;

  return (
    <section className="gps-booking-alert-panel gps-booking-alert-panel--compact" aria-label="Booking alert configuration"
      data-alert-mode={values?.mode || "normal"}>
      <label className="gps-booking-alert-toggle gps-booking-alert-toggle--compact">
        <span><strong>Route deviation alerts</strong><small>Tracking stays active when alerts are off.</small></span>
        <span className="gps-booking-alert-switch">
          <input type="checkbox" role="switch" aria-label="Booking deviation alerts"
            checked={values.alertsEnabled} disabled={!canEdit || saving}
            onChange={(event) => toggleAlerts(event.target.checked)} />
          <b>{values.alertsEnabled ? "ON" : "OFF"}</b>
        </span>
      </label>
      <div className="gps-booking-alert-maximum">
        <label htmlFor={`deviation-maximum-${bookingId}`}>
          <strong>Maximum allowed</strong><small>Minimum {effectiveLimits.MIN_ROUTE_DEVIATION_RADIUS_METERS} m</small>
        </label>
        <span className="gps-booking-alert-number">
          <input id={`deviation-maximum-${bookingId}`} type="number"
            aria-label="Maximum Route Deviation Radius" step="1"
            min={effectiveLimits.MIN_ROUTE_DEVIATION_RADIUS_METERS}
            value={maximumInput} disabled={!canEdit || saving}
            aria-invalid={Boolean(message && !validPayload(values, effectiveLimits, deviationInput, maximumInput))}
            onChange={(event) => updateMaximum(event.target.value)} />
          <span>meters</span>
        </span>
      </div>
      <div className="gps-booking-alert-current">
        <div className="gps-booking-alert-current-head">
          <label htmlFor={`deviation-radius-${bookingId}`}><strong>Alert radius</strong></label>
          <output>{deviationInput} m</output>
          {!canEdit ? <span className="gps-booking-alert-readonly">{authorized ? "Lifecycle locked" : "Read only"}</span> : null}
        </div>
        <input id={`deviation-radius-${bookingId}`} type="range" aria-label="Route deviation radius" step="1"
          min={effectiveLimits.MIN_ROUTE_DEVIATION_RADIUS_METERS}
          max={values.deviationRadiusMaximumMeters}
          value={deviationInput} disabled={!canEdit || saving}
          aria-invalid={Boolean(message && !validPayload(values, effectiveLimits, deviationInput, maximumInput))}
          onChange={(event) => updateDeviation(event.target.value)} />
      </div>
      {message ? <p className="gps-booking-alert-message" role="status">{message}</p> : null}
    </section>
  );
}
